import React, { useEffect, useState } from "react";
import {
  Drawer,
  Box,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/EditOutlined";
import RestoreIcon from "@mui/icons-material/RestoreOutlined";
import AddCommentIcon from "@mui/icons-material/AddCommentOutlined";
import AutorenewIcon from "@mui/icons-material/AutorenewOutlined";
import BusinessIcon from "@mui/icons-material/BusinessOutlined";
import PersonIcon from "@mui/icons-material/PersonOutlined";
import PhoneIcon from "@mui/icons-material/PhoneOutlined";
import PlaceIcon from "@mui/icons-material/PlaceOutlined";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import {
  fetchTicket,
  updateTicketMeta,
  restoreTicket,
  fetchAdminUsersForTickets,
  fetchResolvedByOptions,
  clearCurrent,
} from "./ticketsSlice";
import FileGallery from "./FileGallery";
import TicketUpdateDialog from "./TicketUpdateDialog";

const STATUS_LABELS = {
  open: "Open",
  ongoing: "Ongoing",
  waiting_user_feedback: "Waiting for user feedback",
  waiting_material: "Waiting for material",
  waiting_service: "Waiting for service",
  resolved: "Resolved",
  removed: "Removed",
};

const STATUS_COLOR = {
  open: { bg: "#fdecec", fg: "#bb0000" },
  ongoing: { bg: "#fdf2e6", fg: "#df6e0c" },
  waiting_user_feedback: { bg: "#f3eafd", fg: "#6f3cb1" },
  waiting_material: { bg: "#f3eafd", fg: "#6f3cb1" },
  waiting_service: { bg: "#f3eafd", fg: "#6f3cb1" },
  resolved: { bg: "#e8f5ec", fg: "#107e3e" },
  removed: { bg: "#f1f1f1", fg: "#6a6d70" },
};

const StatusChip = ({ status }) => {
  const c = STATUS_COLOR[status] || STATUS_COLOR.open;
  return (
    <Chip
      label={STATUS_LABELS[status] || status}
      size="small"
      sx={{
        height: 20,
        fontSize: "0.6875rem",
        borderRadius: "2px",
        backgroundColor: c.bg,
        color: c.fg,
        fontWeight: 600,
        "& .MuiChip-label": { px: 0.75 },
      }}
    />
  );
};

const fieldSx = { "& .MuiInputBase-input": { fontSize: "0.8125rem" } };

const fmt = (d) => (d ? dayjs(d).format("DD MMM YYYY · HH:mm") : "—");

// The drawer renders at `zIndex: modal + 1`. Default Select menus pop up
// at `modal` so they appear *behind* the drawer and clicks miss. Force the
// Select popovers above the drawer.
const selectMenuProps = {
  sx: { zIndex: (t) => t.zIndex.modal + 2 },
  PaperProps: { sx: { mt: 0.5 } },
};

export default function TicketDetailDrawer({ ticketId, onClose }) {
  const dispatch = useDispatch();
  const { current, loadingCurrent, adminUsers, resolvedByOptions } =
    useSelector((s) => s.tickets);

  const [updateOpen, setUpdateOpen] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState(null);

  useEffect(() => {
    if (ticketId) {
      dispatch(fetchTicket(ticketId));
      dispatch(fetchAdminUsersForTickets());
      dispatch(fetchResolvedByOptions());
    } else {
      dispatch(clearCurrent());
    }
  }, [ticketId, dispatch]);

  const open = Boolean(ticketId);
  const t = current && current._id === ticketId ? current : null;

  const onChangeStatus = async (next) => {
    if (!t) return;
    if (next === "resolved" && !t.resolvedBy) {
      const rb = window.prompt("Resolved by? (will be saved on the ticket)");
      if (!rb || !rb.trim()) return;
      const res = await dispatch(
        updateTicketMeta({
          id: t._id,
          payload: { status: next, resolvedBy: rb.trim() },
        })
      );
      if (updateTicketMeta.fulfilled.match(res)) toast.success("Marked resolved");
      else toast.error(res.payload || "Failed");
      return;
    }
    const res = await dispatch(
      updateTicketMeta({ id: t._id, payload: { status: next } })
    );
    if (updateTicketMeta.fulfilled.match(res)) toast.success("Status updated");
    else toast.error(res.payload || "Failed");
  };

  const onChangeMeta = async (patch) => {
    if (!t) return;
    const res = await dispatch(updateTicketMeta({ id: t._id, payload: patch }));
    if (!updateTicketMeta.fulfilled.match(res)) {
      toast.error(res.payload || "Failed");
    }
  };

  const onRestore = async () => {
    const res = await dispatch(restoreTicket(t._id));
    if (restoreTicket.fulfilled.match(res)) toast.success("Restored");
    else toast.error(res.payload || "Failed");
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{ zIndex: (t) => t.zIndex.modal + 1 }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 520, md: 600 },
            maxWidth: "100vw",
          },
        }}
      >
        {!t ? (
          <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
            {loadingCurrent ? (
              <CircularProgress size={20} />
            ) : (
              <Typography variant="caption" color="text.secondary">
                Ticket not found.
              </Typography>
            )}
          </Box>
        ) : (
          <>
            <Box
              sx={{
                p: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
                position: "sticky",
                top: 0,
                zIndex: 2,
                backgroundColor: "background.paper",
              }}
            >
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                    <StatusChip status={t.status} />
                    {t.isdeleted && (
                      <Chip
                        label="deleted"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: "0.625rem",
                          borderRadius: "2px",
                          backgroundColor: "#fdecec",
                          color: "#bb0000",
                          "& .MuiChip-label": { px: 0.5 },
                        }}
                      />
                    )}
                    <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                      {t.imei}
                    </Typography>
                  </Stack>
                  <Typography variant="h5" sx={{ wordBreak: "break-word" }}>
                    {t.heading || "—"}
                  </Typography>
                  {t.company?.name && (
                    <Typography variant="caption" color="text.secondary">
                      {t.company.manufacturing_id ? `${t.company.manufacturing_id} · ` : ""}
                      {t.company.name}
                    </Typography>
                  )}
                </Box>
                <Stack
                  direction="row"
                  spacing={0.25}
                  sx={{ flexShrink: 0, alignItems: "center" }}
                >
                  {t.isdeleted && (
                    <Tooltip title="Restore">
                      <IconButton size="small" onClick={onRestore}>
                        <RestoreIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Refresh">
                    <IconButton size="small" onClick={() => dispatch(fetchTicket(t._id))}>
                      <AutorenewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Close">
                    <IconButton
                      onClick={onClose}
                      sx={{
                        ml: 0.25,
                        backgroundColor: "action.hover",
                        "&:hover": { backgroundColor: "error.light", color: "error.dark" },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Box>

            {/* === Company & operator info card (read-only summary) === */}
            {(t.company?.name ||
              t.company?.operator_name ||
              t.company?.operator_phone) && (
              <Box sx={{ px: 1.5, pt: 1.5 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    borderColor: "primary.main",
                    backgroundColor: "primary.light",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.25}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <BusinessIcon sx={{ fontSize: 15, color: "primary.main" }} />
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, fontSize: "0.8125rem", color: "primary.main" }}
                        >
                          {t.company?.name || "—"}
                        </Typography>
                        {t.company?.manufacturing_id != null && (
                          <Chip
                            label={`#${t.company.manufacturing_id}`}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.625rem",
                              fontFamily: "monospace",
                              backgroundColor: "#fff",
                              color: "primary.main",
                              fontWeight: 600,
                            }}
                          />
                        )}
                        {t.deviceType && (
                          <Chip
                            label={t.deviceType === "motor" ? "MOTOR" : "OHT"}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.625rem",
                              fontWeight: 700,
                              backgroundColor:
                                t.deviceType === "motor" ? "#0e7490" : "#0d9488",
                              color: "#fff",
                            }}
                          />
                        )}
                      </Stack>

                      {(t.company?.district || t.company?.block || t.company?.panchayat) && (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <PlaceIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                            {[t.company.district, t.company.block, t.company.panchayat]
                              .filter(Boolean)
                              .join(" · ")}
                          </Typography>
                        </Stack>
                      )}

                      {(t.company?.operator_name || t.company?.operator_phone) && (
                        <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
                          <PersonIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                          <Typography
                            variant="caption"
                            sx={{ fontSize: "0.75rem", color: "text.primary", fontWeight: 500 }}
                          >
                            {t.company.operator_name || "—"}
                          </Typography>
                          {t.company.operator_phone && (
                            <>
                              <PhoneIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                              <Typography
                                variant="caption"
                                sx={{ fontSize: "0.75rem", fontFamily: "monospace" }}
                              >
                                {t.company.operator_phone}
                              </Typography>
                            </>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              </Box>
            )}

            <Box sx={{ p: 1.5, pt: 2.5 }}>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                mb={1.5}
                sx={{
                  // More vertical breathing room on mobile so the floating
                  // labels of the row below don't overlap the row above when
                  // the fields wrap.
                  rowGap: { xs: 2.25, md: 1 },
                }}
              >
                <TextField
                  size="small"
                  select
                  label="Status"
                  value={t.status}
                  onChange={(e) => onChangeStatus(e.target.value)}
                  disabled={t.isdeleted}
                  sx={{
                    ...fieldSx,
                    flex: {
                      xs: "1 1 calc(50% - 4px)",
                      md: "1 1 180px",
                    },
                    minWidth: 0,
                  }}
                  SelectProps={{ MenuProps: selectMenuProps }}
                >
                  {Object.entries(STATUS_LABELS).map(([v, l]) => (
                    <MenuItem key={v} value={v} sx={{ fontSize: "0.8125rem" }}>
                      {l}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  select
                  label="Priority"
                  value={t.priority || "medium"}
                  onChange={(e) => onChangeMeta({ priority: e.target.value })}
                  disabled={t.isdeleted}
                  sx={{
                    ...fieldSx,
                    flex: {
                      xs: "1 1 calc(50% - 4px)",
                      md: "0 1 150px",
                    },
                    minWidth: 0,
                  }}
                  SelectProps={{ MenuProps: selectMenuProps }}
                >
                  <MenuItem value="low" sx={{ fontSize: "0.8125rem" }}>
                    Low
                  </MenuItem>
                  <MenuItem value="medium" sx={{ fontSize: "0.8125rem" }}>
                    Medium
                  </MenuItem>
                  <MenuItem value="high" sx={{ fontSize: "0.8125rem" }}>
                    High
                  </MenuItem>
                  <MenuItem value="critical" sx={{ fontSize: "0.8125rem" }}>
                    Critical
                  </MenuItem>
                </TextField>
                <TextField
                  size="small"
                  select
                  label="Responsible"
                  value={t.responsiblePerson?.id || ""}
                  onChange={(e) =>
                    onChangeMeta({ responsiblePersonId: e.target.value })
                  }
                  disabled={t.isdeleted}
                  sx={{
                    ...fieldSx,
                    flex: {
                      xs: "1 1 100%",
                      md: "1 1 220px",
                    },
                    minWidth: 0,
                  }}
                  SelectProps={{ MenuProps: selectMenuProps }}
                >
                  <MenuItem value="" sx={{ fontSize: "0.8125rem" }}>—</MenuItem>
                  {adminUsers.map((u) => (
                    <MenuItem key={u._id} value={u._id} sx={{ fontSize: "0.8125rem" }}>
                      {u.name || u.username || "—"}
                    </MenuItem>
                  ))}
                </TextField>
                {/* Resolved By is only relevant when the ticket is closed
                    out as "resolved". Hide it for every other status so the
                    edit form stays focused on what matters. */}
                {t.status === "resolved" && (
                  <TextField
                    size="small"
                    select
                    label="Resolved By"
                    value={t.resolvedBy || ""}
                    onChange={(e) =>
                      onChangeMeta({ resolvedBy: e.target.value })
                    }
                    disabled={t.isdeleted}
                    sx={{
                      ...fieldSx,
                      flex: {
                        xs: "1 1 100%",
                        md: "1 1 180px",
                      },
                      minWidth: 0,
                    }}
                    SelectProps={{ MenuProps: selectMenuProps }}
                  >
                    <MenuItem value="" sx={{ fontSize: "0.8125rem" }}>—</MenuItem>
                    {resolvedByOptions.map((o) => (
                      <MenuItem
                        key={o._id || o.name}
                        value={o.name}
                        sx={{ fontSize: "0.8125rem" }}
                      >
                        {o.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>
              <Stack direction="row" spacing={2} mb={1.5} flexWrap="wrap">
                <Typography variant="caption" color="text.secondary">
                  Created {fmt(t.createdAt)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Updated {fmt(t.updatedAt)}
                </Typography>
                {t.createdBy?.username && (
                  <Typography variant="caption" color="text.secondary">
                    by {t.createdBy.username}
                  </Typography>
                )}
              </Stack>

              <Divider sx={{ mb: 1.5 }} />

              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>
                  Updates ({t.updates?.length || 0})
                </Typography>
                {!t.isdeleted && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddCommentIcon fontSize="small" />}
                    onClick={() => {
                      setEditingUpdate(null);
                      setUpdateOpen(true);
                    }}
                    sx={{ fontSize: "0.6875rem", py: 0.25, minHeight: 0 }}
                  >
                    Add update
                  </Button>
                )}
              </Stack>

              <Stack spacing={1}>
                {/* Chat-style: oldest at top, newest at bottom. Just render
                    the array in its natural (chronological) order. */}
                {(t.updates || []).map((u) => {
                  const isChat = !u.system;
                  return (
                  <Paper
                    key={u._id}
                    variant="outlined"
                    sx={{
                      p: 1,
                      borderColor: isChat ? "#10b981" : "divider",
                      backgroundColor: isChat ? "#10b981" : "action.hover",
                      color: isChat ? "#ffffff" : "inherit",
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        {u.system && (
                          <Chip
                            label="system"
                            size="small"
                            sx={{
                              height: 16,
                              fontSize: "0.625rem",
                              borderRadius: "2px",
                              backgroundColor: "#f1f1f1",
                              "& .MuiChip-label": { px: 0.5 },
                            }}
                          />
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: isChat ? "#ffffff" : "text.primary",
                          }}
                        >
                          {u.actor?.name || u.actor?.username || "—"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: isChat ? "rgba(255,255,255,0.78)" : "text.secondary",
                          }}
                        >
                          {fmt(u.createdAt)}
                        </Typography>
                      </Stack>
                      {!u.system && !t.isdeleted && (
                        <Tooltip title="Edit update">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingUpdate(u);
                              setUpdateOpen(true);
                            }}
                            sx={{
                              color: "rgba(255,255,255,0.85)",
                              "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                    {u.text && (
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: "pre-wrap",
                          fontSize: "0.8125rem",
                          mb: u.files?.length ? 0.75 : 0,
                          color: isChat ? "#ffffff" : "text.primary",
                        }}
                      >
                        {u.text}
                      </Typography>
                    )}
                    {u.files?.length > 0 && (
                      <FileGallery
                        existing={u.files}
                        readOnly
                        emptyText=""
                      />
                    )}
                  </Paper>
                  );
                })}
                {(!t.updates || t.updates.length === 0) && (
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                    No updates yet.
                  </Typography>
                )}
              </Stack>
            </Box>
          </>
        )}
      </Drawer>

      <TicketUpdateDialog
        open={updateOpen}
        ticketId={t?._id}
        editingUpdate={editingUpdate}
        onClose={() => {
          setUpdateOpen(false);
          setEditingUpdate(null);
        }}
      />

    </>
  );
}
